import React, { useCallback, useEffect, useState } from "react";
import { fetchVariance, getCurrency, fetchCostDataQuality, logWaste, fetchIngredients, fetchFoodCostTrend } from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

const WASTE_REASONS = ["spoilage", "over_prep", "trim", "breakage", "staff_meal", "comp", "other"];

const MS = 1000;
const ET_MS = 3 * 3600 * MS;
const localStr = (ms) => new Date(ms + ET_MS).toISOString().slice(0, 16);
const startOfLocalDay = (ms) => localStr(ms).slice(0, 10) + "T00:00";

// F&B Cost Control — Controller variance: theoretical (recipes x sales) vs
// actual (chef-logged) ingredient usage, with cost impact + food cost %.
export default function VariancePage() {
  const { t } = useT();
  // The i18n lookup returns the key itself when a string is missing, so
  // `t(k) || default` never falls back. Treat "key unchanged" as missing and
  // use the English default (AM/OM come in the language review).
  const tt = (k, d) => { const v = t(k); return (v && v !== k) ? v : d; };
  const cur = getCurrency() || "ETB";
  const now = Date.now();
  const [start, setStart] = useState(startOfLocalDay(now));
  const [end, setEnd] = useState(localStr(now));
  const [data, setData] = useState(null);
  const [dq, setDq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [ings, setIngs] = useState([]);
  const [wf, setWf] = useState({ ingredient_id: "", quantity: "", reason: "spoilage" });
  const [wMsg, setWMsg] = useState("");
  const [trend, setTrend] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      setData(await fetchVariance({ start, end }));
      fetchCostDataQuality({ start, end }).then(setDq).catch(() => setDq(null));
    }
    catch (e) { setErr(e?.response?.data?.detail || t("cc_could_not_load")); }
    finally { setLoading(false); }
  }, [start, end]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  useEffect(() => { fetchIngredients().then(setIngs).catch(() => setIngs([])); }, []);
  useEffect(() => { fetchFoodCostTrend().then(setTrend).catch(() => setTrend(null)); }, []);

  const submitWaste = async () => {
    setWMsg("");
    if (!wf.ingredient_id || !(Number(wf.quantity) > 0)) { setWMsg(tt("cc_waste_fill", "Pick an ingredient and quantity.")); return; }
    try {
      await logWaste({ ingredient_id: Number(wf.ingredient_id), quantity: Number(wf.quantity), reason: wf.reason, location: "kitchen" });
      setWf({ ingredient_id: "", quantity: "", reason: "spoilage" });
      setWMsg("✓");
      load();
    } catch (e) { setWMsg(e?.response?.data?.detail || t("cc_could_not_load")); }
  };

  const preset = (which) => {
    const n = Date.now();
    if (which === "today") { setStart(startOfLocalDay(n)); setEnd(localStr(n)); }
    else if (which === "yesterday") { const y = n - 24 * 3600 * MS; setStart(startOfLocalDay(y)); setEnd(startOfLocalDay(n)); }
    else if (which === "7d") { setStart(startOfLocalDay(n - 6 * 24 * 3600 * MS)); setEnd(localStr(n)); }
  };

  const money = (v) => formatPrice(v || 0, cur);
  const tot = data && data.totals;
  const pctColor = (p) => p == null ? "#5f6b78" : p > 40 ? "#A32D2D" : p > 33 ? "#854F0B" : "#1D9E75";
  // Gross profit after food cost = revenue − food cost (actual when logged, else
  // theoretical). Honest "what's left after ingredients" — before labour/overhead.
  const foodCost = tot ? (tot.actual_entered ? tot.actual_cogs : tot.theoretical_cogs) : 0;
  const gross = tot ? (tot.revenue || 0) - (foodCost || 0) : 0;
  const grossPct = tot && tot.revenue ? Math.round((gross / tot.revenue) * 1000) / 10 : null;

  return (
    <>
      <h1>📊 {t("cc_nav_variance")} <span className="muted" style={{ fontSize: 14 }}>({t("cc_var_subtitle")})</span></h1>
      <p className="muted" style={{ marginTop: -4, maxWidth: 640 }}>
        {t("cc_var_intro")}
      </p>

      {trend && trend.series.some((s) => s.actual_pct != null) && (() => {
        const scale = 1.4;                    // px per % (a 50% day ≈ 70px)
        const targetY = Math.min(90, trend.target_pct * scale);
        const sm = trend.summary;
        return (
          <div className="card" style={{ marginTop: 10, padding: 12, maxWidth: 760 }}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
              <b style={{ color: "#063b48" }}>{tt("cc_trend_title", "Food cost trend — last 14 days")}</b>
              <span style={{ fontSize: 12, color: sm.days_over_target > 0 ? "#A32D2D" : "#1D7A44" }}>
                {sm.days_over_target} of {sm.days_with_sales} days over your {trend.target_pct}% target
                {sm.avg_actual_pct != null ? ` · avg ${sm.avg_actual_pct}%` : ""}
              </span>
            </div>
            <div style={{ position: "relative", height: 96, display: "flex", alignItems: "flex-end", gap: 3, borderBottom: "1px solid #e3ebe9" }}>
              <div style={{ position: "absolute", left: 0, right: 0, bottom: targetY, borderTop: "1px dashed #854F0B" }} />
              <div style={{ position: "absolute", right: 2, bottom: targetY + 1, fontSize: 10, color: "#854F0B" }}>{trend.target_pct}%</div>
              {trend.series.map((s) => {
                const pct = s.actual_pct;
                const h = pct == null ? 3 : Math.max(4, Math.min(94, pct * scale));
                return (
                  <div key={s.date} title={`${s.date}: ${pct == null ? "no data" : pct + "%"}`}
                    style={{ flex: 1, height: h, borderRadius: "3px 3px 0 0",
                      background: pct == null ? "#e3ebe9" : s.over_target ? "#A32D2D" : "#1D9E75" }} />
                );
              })}
            </div>
          </div>
        );
      })()}

      <div className="card" style={{ marginTop: 10, padding: 12, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("cc_from")}<br />
          <input type="datetime-local" value={start} max={end} onChange={(e) => setStart(e.target.value)} />
        </label>
        <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("cc_to")}<br />
          <input type="datetime-local" value={end} min={start} onChange={(e) => setEnd(e.target.value)} />
        </label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button className="btn secondary small" onClick={() => preset("today")}>{t("cc_today")}</button>
          <button className="btn secondary small" onClick={() => preset("yesterday")}>{t("cc_yesterday")}</button>
          <button className="btn secondary small" onClick={() => preset("7d")}>{t("cc_last_7_days")}</button>
        </div>
      </div>

      {dq && !dq.ok && (
        <div className="card" style={{ marginTop: 10, padding: 12, background: "#FFF8EC", borderColor: "#f0d38b", fontSize: 13, maxWidth: 760 }}>
          <b style={{ color: "#854F0B" }}>⚠ {tt("cc_dq_title", "Data quality — numbers may be understated")}</b>
          {dq.missing_recipes.length > 0 && (
            <div style={{ marginTop: 6 }}>
              {tt("cc_dq_missing_recipes", "Sold dishes with no recipe (counted as zero cost)")}: {dq.missing_recipes.slice(0, 8).map((m) => m.name).join(", ")}
              {dq.missing_recipes.length > 8 ? ` +${dq.missing_recipes.length - 8}` : ""}
            </div>
          )}
          {dq.zero_cost_ingredients.length > 0 && (
            <div style={{ marginTop: 6 }}>
              {tt("cc_dq_zero_cost", "Recipe ingredients with zero cost")}: {dq.zero_cost_ingredients.slice(0, 8).map((z) => z.name).join(", ")}
              {dq.zero_cost_ingredients.length > 8 ? ` +${dq.zero_cost_ingredients.length - 8}` : ""}
            </div>
          )}
        </div>
      )}

      <div className="card" style={{ marginTop: 10, padding: 12, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", maxWidth: 760 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#063b48", width: "100%" }}>{tt("cc_log_waste", "Log waste / spoilage")}</div>
        <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("cc_ingredient")}<br />
          <select value={wf.ingredient_id} onChange={(e) => setWf({ ...wf, ingredient_id: e.target.value })}>
            <option value="">—</option>
            {ings.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 12, color: "#5f6b78" }}>{tt("cc_qty", "Qty")}<br />
          <input type="number" min="0" step="any" style={{ width: 90 }} value={wf.quantity} onChange={(e) => setWf({ ...wf, quantity: e.target.value })} />
        </label>
        <label style={{ fontSize: 12, color: "#5f6b78" }}>{tt("cc_reason", "Reason")}<br />
          <select value={wf.reason} onChange={(e) => setWf({ ...wf, reason: e.target.value })}>
            {WASTE_REASONS.map((r) => <option key={r} value={r}>{tt("cc_waste_" + r, r.replace("_", " "))}</option>)}
          </select>
        </label>
        <button className="btn primary small" onClick={submitWaste}>{tt("cc_log", "Log")}</button>
        {wMsg && <span style={{ fontSize: 12, color: wMsg === "✓" ? "#1D9E75" : "#A32D2D" }}>{wMsg === "✓" ? "✓ " + (tt("cc_saved", "Saved")) : wMsg}</span>}
      </div>

      {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}
      {loading ? <p className="muted" style={{ marginTop: 12 }}>{t("loading")}</p> : data ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px,1fr))", gap: 12, marginTop: 12, maxWidth: 900 }}>
            <Kpi label={t("cc_orders")} value={tot.orders ?? 0} />
            <Kpi label={t("cc_revenue")} value={money(tot.revenue)} />
            <Kpi label={t("cc_gross_profit")} value={money(gross)} sub={grossPct == null ? null : `${grossPct}%`} color="#1D9E75" />
            <Kpi label={t("cc_issued_food")} value={tot.issued_entered ? money(tot.issued_cogs) : "—"} color="#063b48" />
            <Kpi label={t("cc_theo_food")} value={money(tot.theoretical_cogs)} sub={tot.theoretical_food_cost_pct == null ? null : `${tot.theoretical_food_cost_pct}%`} color={pctColor(tot.theoretical_food_cost_pct)} />
            <Kpi label={t("cc_actual_food")} value={tot.actual_entered ? money(tot.actual_cogs) : "—"} sub={tot.actual_entered && tot.actual_food_cost_pct != null ? `${tot.actual_food_cost_pct}%` : null} color={pctColor(tot.actual_food_cost_pct)} />
            <Kpi label={t("cc_variance_cost")} value={tot.actual_entered ? money(tot.variance_cost) : "—"} color={tot.variance_cost > 0 ? "#A32D2D" : "#1D9E75"} />
            <Kpi label={t("cc_returned")} value={tot.returned_entered ? money(tot.returned_cogs) : "—"} color="#063b48" />
            <Kpi label={tt("cc_waste", "Waste (logged)")} value={money(tot.waste_cogs)} color={tot.waste_cogs > 0 ? "#854F0B" : "#1D9E75"} />
            <Kpi label={t("cc_loss")} value={tot.returned_entered ? money(tot.loss_cogs) : "—"} color={tot.loss_cogs > 0 ? "#A32D2D" : "#1D9E75"} />
          </div>
          {tot.actual_entered && (
            <div style={{ marginTop: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 10,
                background: tot.usage_confirmed ? "#e7f6ec" : "#fff4e0", color: tot.usage_confirmed ? "#1D9E75" : "#854F0B" }}>
                {tot.usage_confirmed ? "✓ " + t("cc_confirmed") : t("cc_pending")}
              </span>
            </div>
          )}
          <p className="muted" style={{ fontSize: 11, marginTop: 6, maxWidth: 760 }}>{t("cc_gross_profit_note")}</p>
          {!tot.actual_entered && <div className="card" style={{ marginTop: 10, background: "#FFF8EC", borderColor: "#f0d38b", fontSize: 13, maxWidth: 760 }}>{t("cc_no_actual_notice")}</div>}

          <div className="card" style={{ marginTop: 12, overflowX: "auto", maxWidth: 760 }}>
            {data.rows.length === 0 ? <p className="muted">{t("cc_var_no_rows")}</p> : (
              <table className="data" style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr style={{ background: "#008080" }}>
                  <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("cc_ingredient")}</th>
                  <th style={{ color: "#fff", textAlign: "right", padding: "8px 10px" }}>{t("cc_issued")}</th>
                  <th style={{ color: "#fff", textAlign: "right", padding: "8px 10px" }}>{t("cc_should_theory")}</th>
                  <th style={{ color: "#fff", textAlign: "right", padding: "8px 10px" }}>{t("cc_actual")}</th>
                  <th style={{ color: "#fff", textAlign: "right", padding: "8px 10px" }}>{t("cc_nav_variance")}</th>
                  <th style={{ color: "#fff", textAlign: "right", padding: "8px 10px" }}>{t("cc_returned")}</th>
                  <th style={{ color: "#fff", textAlign: "right", padding: "8px 10px" }}>{tt("cc_waste", "Waste")}</th>
                  <th style={{ color: "#fff", textAlign: "right", padding: "8px 10px" }}>{t("cc_loss")}</th>
                  <th style={{ color: "#fff", textAlign: "right", padding: "8px 10px" }}>{t("cc_cost_impact")}</th>
                </tr></thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={r.ingredient_id} style={{ background: i % 2 ? "#f2f9f8" : "#fff" }}>
                      <td style={{ padding: "6px 10px" }}><b>{r.name}</b></td>
                      <td style={{ padding: "6px 10px", textAlign: "right" }}>{r.has_issued ? `${r.issued_qty} ${r.unit}` : "—"}</td>
                      <td style={{ padding: "6px 10px", textAlign: "right" }}>{r.theoretical_qty} {r.unit}</td>
                      <td style={{ padding: "6px 10px", textAlign: "right" }}>{r.has_actual ? `${r.actual_qty} ${r.unit}` : "—"}</td>
                      <td style={{ padding: "6px 10px", textAlign: "right", color: r.variance_qty > 0 ? "#A32D2D" : r.variance_qty < 0 ? "#1D9E75" : "#5f6b78" }}>
                        {r.has_actual ? `${r.variance_qty > 0 ? "+" : ""}${r.variance_qty} ${r.unit}` : "—"}
                      </td>
                      <td style={{ padding: "6px 10px", textAlign: "right" }}>{r.has_returned ? `${r.returned_qty} ${r.unit}` : "—"}</td>
                      <td style={{ padding: "6px 10px", textAlign: "right", color: r.has_waste ? "#854F0B" : "#5f6b78" }}>{r.has_waste ? `${r.waste_qty} ${r.unit}` : "—"}</td>
                      <td style={{ padding: "6px 10px", textAlign: "right", color: r.loss_qty > 0.001 ? "#A32D2D" : "#5f6b78" }}>{r.has_issued ? `${r.loss_qty} ${r.unit}` : "—"}</td>
                      <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 600, color: r.variance_cost > 0 ? "#A32D2D" : r.variance_cost < 0 ? "#1D9E75" : "#5f6b78" }}>
                        {r.has_actual ? money(r.variance_cost) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}
    </>
  );
}

function Kpi({ label, value, color, sub }) {
  return (
    <div className="card" style={{ padding: "12px 14px" }}>
      <div className="muted" style={{ fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || "#063b48" }}>{value}</div>
      {sub != null && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
